/**
 * Composable para gerenciar branding dinâmico
 * Carrega logos, ícones e favicon customizados de panel_settings
 */

import { ref, computed } from 'vue'
import { apiService } from '@/services/api'
import { STORAGE_KEYS } from '@/utils/storageKeys'

/** TTL do cache de metadados no localStorage (24 horas) */
const BRANDING_CACHE_TTL_MS = 24 * 60 * 60 * 1000

const DEFAULT_LOGO = '/favicon.png'
const DEFAULT_BRANDING = {
  panel_name: 'Veltrix Panel',
  panel_logo: '',
  dashboard_logo: '',
  login_logo: '',
  favicon_svg: '',
  favicon_png: '',
  app_icon_192: '',
  app_icon_512: '',
  apple_touch_icon: '',
  og_image: '',
  theme_primary_color: '#6366f1',
  theme_secondary_color: '#8b5cf6',
  pwa_name: 'Veltrix Panel',
  pwa_short_name: 'Veltrix',
  pwa_description: 'Modern SSH & V2Ray Management System',
  assets_version: 0,
}

const cacheVersion = ref(1)

const applyAssetsVersion = (version) => {
  const v = Number(version)
  if (Number.isFinite(v) && v > 0) {
    cacheVersion.value = v
  }
}

/** Remove query/hash para URLs estáveis (evita loop de re-fetch no <img>). */
const normalizeAssetPath = (path) => {
  if (!path || typeof path !== 'string') return ''
  const trimmed = path.trim()
  if (!trimmed) return ''

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const parsed = new URL(trimmed)
      return `${parsed.origin}${parsed.pathname}`
    } catch {
      return trimmed.split(/[?#]/)[0]
    }
  }

  return trimmed.split(/[?#]/)[0]
}

const resolveAssetsVersion = () => {
  const apiVersion = Number(brandingData.value?.assets_version)
  if (Number.isFinite(apiVersion) && apiVersion > 0) {
    return apiVersion
  }
  return cacheVersion.value
}

const loadFromCacheSync = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.BRANDING)
    if (cached) {
      const data = JSON.parse(cached)
      if (data.timestamp && Date.now() - data.timestamp < BRANDING_CACHE_TTL_MS && data.branding) {
        applyAssetsVersion(data.assets_version ?? data.branding?.assets_version)
        return data.branding
      }
    }
  } catch {
    // usa padrão
  }
  return null
}

const brandingData = ref(loadFromCacheSync())
const loading = ref(false)
const error = ref(null)
let brandingApiPromise = null

const manifestHrefFromBase = (version) => {
  const base = import.meta.env.BASE_URL || '/'
  const url = new URL('api/branding/pwa-manifest', new URL(base, window.location.origin))
  url.searchParams.set('v', String(version ?? resolveAssetsVersion()))
  return url.href
}

const refreshPwaManifestLink = (version) => {
  if (typeof document === 'undefined') return
  const manifestLink = document.querySelector('link[rel="manifest"]')
  if (!manifestLink) return
  manifestLink.href = manifestHrefFromBase(version)
}

const hasCustomAsset = (path) => typeof path === 'string' && path.trim() !== ''

export function useBranding() {
  const saveToCache = (data) => {
    try {
      const assetsVersion = data?.assets_version ?? cacheVersion.value
      localStorage.setItem(
        STORAGE_KEYS.BRANDING,
        JSON.stringify({
          branding: data,
          assets_version: assetsVersion,
          timestamp: Date.now(),
        })
      )
    } catch (err) {
      console.warn('[useBranding] Erro ao salvar cache:', err)
    }
  }

  const syncFromApiPayload = (data) => {
    brandingData.value = data
    applyAssetsVersion(data?.assets_version)
    saveToCache(data)
  }

  const loadFromAPI = async () => {
    if (brandingApiPromise) {
      return brandingApiPromise
    }

    loading.value = true
    error.value = null

    brandingApiPromise = (async () => {
      try {
        const response = await apiService.branding.get()

        if (response.success && response.data) {
          syncFromApiPayload(response.data)
        } else {
          brandingData.value = { ...DEFAULT_BRANDING }
        }
      } catch (err) {
        error.value = err.message
        console.error('[useBranding] Erro ao carregar da API:', err)
        brandingData.value = { ...DEFAULT_BRANDING }
      } finally {
        loading.value = false
        brandingApiPromise = null
      }

      return brandingData.value
    })()

    return brandingApiPromise
  }

  const loadBranding = async (forceReload = false) => {
    if (brandingData.value && !forceReload) {
      return brandingData.value
    }
    return await loadFromAPI()
  }

  const getFavicon = computed(() => {
    return brandingData.value?.favicon_svg || brandingData.value?.favicon_png || DEFAULT_LOGO
  })

  const resolveBrandingPath = (path, staticFallback) => {
    if (!hasCustomAsset(path)) {
      return staticFallback
    }
    if (path.startsWith('/') || path.startsWith('http')) {
      return path
    }
    return `/api/branding/image/${path}`
  }

  const getDashboardLogo = computed(() => {
    return resolveBrandingPath(brandingData.value?.dashboard_logo, '/dashboard.png')
  })

  const getLoginLogo = computed(() => {
    return resolveBrandingPath(brandingData.value?.login_logo, '/login.png')
  })

  const getPanelName = computed(() => {
    return brandingData.value?.panel_name || DEFAULT_BRANDING.panel_name
  })

  const getPanelShortName = computed(() => {
    return brandingData.value?.pwa_short_name || 'Veltrix'
  })

  const getThemeColor = computed(() => {
    return brandingData.value?.theme_primary_color || DEFAULT_BRANDING.theme_primary_color
  })

  const removeExistingIconLinks = () => {
    document
      .querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"], link[rel="shortcut icon"]')
      .forEach((el) => el.remove())
  }

  const updateFavicon = (customIcons = null) => {
    const icons = customIcons || brandingData.value || {}
    const version = resolveAssetsVersion()
    const isSvgAsset = (path) => typeof path === 'string' && /\.svg($|\?)/i.test(path)

    removeExistingIconLinks()

    const faviconPrimary = hasCustomAsset(icons.favicon_png)
      ? icons.favicon_png
      : icons.favicon_svg

    if (hasCustomAsset(faviconPrimary)) {
      const type = isSvgAsset(faviconPrimary) ? 'image/svg+xml' : 'image/png'
      updateLinkTag('icon', type, getImageUrl(faviconPrimary), null)
      if (!isSvgAsset(faviconPrimary)) {
        updateLinkTag('icon', 'image/png', getImageUrl(faviconPrimary), '32x32')
      }
    } else if (hasCustomAsset(icons.app_icon_192)) {
      updateLinkTag('icon', 'image/png', getImageUrl(icons.app_icon_192), '32x32')
    }

    if (hasCustomAsset(icons.app_icon_192)) {
      updateLinkTag('icon', 'image/png', getImageUrl(icons.app_icon_192), '192x192')
    }

    if (hasCustomAsset(icons.apple_touch_icon)) {
      updateLinkTag('apple-touch-icon', null, getImageUrl(icons.apple_touch_icon), '180x180')
    }

    if (hasCustomAsset(icons.og_image)) {
      const absoluteOgUrl = getImageUrl(icons.og_image, true)
      updateMetaTag('og:image', absoluteOgUrl)
      updateMetaTag('twitter:image', absoluteOgUrl)
      updateMetaTag('og:image:secure_url', absoluteOgUrl)
    }

    refreshPwaManifestLink(version)
  }

  const updateLinkTag = (rel, type, href, sizes = null) => {
    if (!href) return

    const link = document.createElement('link')
    link.rel = rel
    if (type) link.type = type
    if (sizes) link.sizes = sizes
    link.href = href
    document.head.appendChild(link)
  }

  const updateMetaTag = (property, content) => {
    let meta = document.querySelector(`meta[property='${property}']`)
    if (!meta) {
      meta = document.querySelector(`meta[name='${property}']`)
    }
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('property', property)
      document.head.appendChild(meta)
    }
    meta.content = content
  }

  const updateTitle = (title) => {
    document.title = title
  }

  const updateThemeColor = (color) => {
    let meta = document.querySelector("meta[name='theme-color']")
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'theme-color'
      document.head.appendChild(meta)
    }
    meta.content = color
  }

  const applyBranding = async () => {
    const branding = await loadFromAPI()

    if (!branding) {
      console.warn('⚠️ [applyBranding] Branding não foi carregado')
      return
    }

    updateFavicon(branding)

    if (branding.panel_name) {
      updateTitle(branding.panel_name)
    }

    if (branding.theme_primary_color) {
      updateThemeColor(branding.theme_primary_color)
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('branding:updated', { detail: branding }))
    }
  }

  const clearCache = () => {
    brandingData.value = null
    try {
      localStorage.removeItem(STORAGE_KEYS.BRANDING)
    } catch (err) {
      console.warn('[useBranding] Erro ao limpar cache:', err)
    }
  }

  const reloadBranding = async () => {
    clearCache()
    const branding = await loadFromAPI()
    updateFavicon(branding)

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('branding:updated', { detail: branding }))
    }

    return branding
  }

  const getImageUrl = (path, forceAbsolute = false) => {
    const cleanPath = normalizeAssetPath(path)
    if (!cleanPath) {
      return ''
    }

    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
      return cleanPath
    }

    const version = resolveAssetsVersion()
    const withVersion = (url) => {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}v=${version}`
    }

    if (cleanPath.startsWith('/api/branding/image/')) {
      return withVersion(cleanPath)
    }

    if (cleanPath.startsWith('/uploads/branding/')) {
      const filename = cleanPath.split('/').pop()
      return withVersion(`/api/branding/image/${filename}`)
    }

    if (cleanPath.startsWith('/uploads/')) {
      return withVersion(cleanPath)
    }

    if (forceAbsolute) {
      return withVersion(`${window.location.origin}${cleanPath}`)
    }

    if (cleanPath.startsWith('/')) {
      return withVersion(cleanPath)
    }

    return withVersion(`/api/branding/image/${cleanPath}`)
  }

  return {
    branding: brandingData,
    loading,
    error,
    loadBranding,
    reloadBranding,
    getFavicon,
    getDashboardLogo,
    getLoginLogo,
    getPanelName,
    getPanelShortName,
    getThemeColor,
    applyBranding,
    updateFavicon,
    updateTitle,
    updateThemeColor,
    getImageUrl,
    clearCache,
  }
}
